/**
 * Chat Target Types
 *
 * Types for configuring a Chat Target — a saved, org-scoped description of
 * how to drive one turn of a conversation against a customer's AI chat
 * endpoint (bootstrap a conversation, send a message, parse the response).
 * Transports are a first-class adapter interface hidden from the end user:
 * the setup wizard detects and writes this config, and the manual JSON
 * editor is the escape hatch.
 *
 * @see docs/features/chat-evals.md
 */

import { z } from 'zod';

/** How a fresh conversation's ID is obtained. */
export const ConversationIdSourceSchema = z.union([
  z.literal('client-uuid'),
  z.object({ responsePath: z.string().min(1) }),
]);

export type ConversationIdSource = z.infer<typeof ConversationIdSourceSchema>;

/**
 * How a conversation is started. Omit `bootstrap` entirely when the first
 * message implicitly creates the conversation (e.g. a client-minted UUID
 * sent as a header on the first message request).
 */
export const ChatTargetBootstrapSchema = z.object({
  urlTemplate: z.string().min(1),
  method: z.string().min(1),
  headersTemplate: z.record(z.string(), z.string()).optional(),
  bodyTemplate: z.string().optional(),
  conversationIdSource: ConversationIdSourceSchema,
});

export type ChatTargetBootstrap = z.infer<typeof ChatTargetBootstrapSchema>;

/** How prior turns are threaded into each new message request. */
export const ChatHistoryModeSchema = z.union([z.literal('full'), z.literal('single')]);

export type ChatHistoryMode = z.infer<typeof ChatHistoryModeSchema>;

/**
 * How to send a single chat message. `bodyTemplate` and `headersTemplate`
 * support the slots `{{message}}`, `{{messages}}`, `{{conversationId}}`, plus
 * the existing `$var.*` / macro resolution used by api_sequence requests
 * (`apps/api/src/workflows/execution/api-sequence-template-resolution.ts`).
 */
export const ChatTargetMessageSchema = z.object({
  urlTemplate: z.string().min(1),
  method: z.string().min(1),
  headersTemplate: z.record(z.string(), z.string()).optional(),
  bodyTemplate: z.string().min(1),
  historyMode: ChatHistoryModeSchema,
});

export type ChatTargetMessage = z.infer<typeof ChatTargetMessageSchema>;

/**
 * How to parse a chat response into text. A discriminated union so adding a
 * transport adapter is "add a member here + one file under
 * `apps/api/src/chat-targets/transports/`" — never a user-facing choice.
 */
export const ChatResponseConfigSchema = z.discriminatedUnion('transport', [
  z.object({ transport: z.literal('ai-sdk-v5') }),
  z.object({
    transport: z.literal('sse'),
    deltaPath: z.string().min(1),
    donePath: z.string().optional(),
  }),
  z.object({
    transport: z.literal('json'),
    textPath: z.string().min(1),
  }),
]);

export type ChatResponseConfig = z.infer<typeof ChatResponseConfigSchema>;

export const DEFAULT_CHAT_TARGET_TIMEOUT_SECONDS = 120;

export const ChatTargetConfigSchema = z.object({
  bootstrap: ChatTargetBootstrapSchema.optional(),
  message: ChatTargetMessageSchema,
  response: ChatResponseConfigSchema,
  timeoutSeconds: z.number().int().positive().default(DEFAULT_CHAT_TARGET_TIMEOUT_SECONDS),
});

export type ChatTargetConfig = z.infer<typeof ChatTargetConfigSchema>;

/** Severity of a chat assertion/invariant verdict, mapped into the existing
 * assertion vocabulary (`warn` = non-blocking, `fail` = step failure). */
export const ChatAssertionSeveritySchema = z.union([z.literal('warn'), z.literal('fail')]);

export type ChatAssertionSeverity = z.infer<typeof ChatAssertionSeveritySchema>;

/**
 * A suite-level rubric judged against every assistant response on a Chat
 * Target (e.g. "the agent never divulges data across RBAC boundaries").
 */
export const GlobalInvariantSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  rubric: z.string().min(1),
  severity: ChatAssertionSeveritySchema,
});

export type GlobalInvariant = z.infer<typeof GlobalInvariantSchema>;

/** Result of parsing a chat transport's raw response body into text. */
export interface ChatParsedResponse {
  text: string;
  parts?: unknown[];
  raw?: unknown;
}
